import { countWords } from './word-count';

const HEADING_SELECTOR = 'h1,h2,h3,h4,h5,h6';

function headingLevel(heading: Element): number {
  return Number(heading.tagName.charAt(1));
}

export function htmlToPlainText(html: string): string {
  if (!html || typeof DOMParser === 'undefined') {
    return '';
  }
  return new DOMParser().parseFromString(html, 'text/html').body.textContent ?? '';
}

export interface FirstChapter {
  text: string;
  wordCount: number;
}

export function extractFirstChapter(html: string): FirstChapter | null {
  if (!html || typeof DOMParser === 'undefined') {
    return null;
  }

  const doc = new DOMParser().parseFromString(html, 'text/html');
  const headings = Array.from(doc.body.querySelectorAll(HEADING_SELECTOR));
  if (headings.length === 0) {
    return null;
  }

  const firstHeading = headings[0];
  const firstLevel = headingLevel(firstHeading);
  const boundaryHeading =
    headings.find((heading, index) => index > 0 && headingLevel(heading) <= firstLevel) ?? null;

  const range = doc.createRange();
  range.setStartBefore(firstHeading);
  if (boundaryHeading) {
    range.setEndBefore(boundaryHeading);
  } else if (doc.body.lastChild) {
    range.setEndAfter(doc.body.lastChild);
  } else {
    range.setEndAfter(firstHeading);
  }

  const fragment = range.cloneContents();
  const text = Array.from(fragment.childNodes)
    .map((node) => node.textContent ?? '')
    .join(' ')
    .trim();

  return { text, wordCount: countWords(text) };
}

export function extractFirstChapterWordCount(html: string): number | null {
  return extractFirstChapter(html)?.wordCount ?? null;
}

export type FirstChapterScope = 'first_chapter' | 'first_chapter_opening' | 'opening';

export const MIN_FIRST_CHAPTER_WORDS = 500;
export const MAX_FIRST_CHAPTER_WORDS = 1500;

export function getFirstChapterScope(wordCount: number | null): FirstChapterScope {
  if (wordCount === null) {
    return 'opening';
  }
  if (wordCount > MAX_FIRST_CHAPTER_WORDS) {
    return 'first_chapter_opening';
  }
  if (wordCount >= MIN_FIRST_CHAPTER_WORDS) {
    return 'first_chapter';
  }
  return 'opening';
}

export const FIRST_CHAPTER_SCOPE_LABEL_KEYS: Record<FirstChapterScope, string> = {
  first_chapter: 'wizard_import_scope_first_chapter',
  first_chapter_opening: 'wizard_import_scope_first_chapter_opening',
  opening: 'wizard_import_scope_opening',
};
